import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import globalStyles from "../../global";
import { getResponsiveHeight, getResponsiveSize } from "../../utils/responsive";
import Footer from '../globalCountries/countryhomepagescomponent/Footer';
import News from '../globalCountries/countryhomepagescomponent/News';
import FreeDeliveryBanner from '../globalCountries/countryhomepagescomponent/FreeDeliveryBanner';
import PromoSection from "../globalCountries/countryhomepagescomponent/PromoSection";
import ServicesSection from "../globalCountries/countryhomepagescomponent/ServicesSection";
import { storesAPI } from "../../utils/api";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const useGoogleMaps = (apiKey) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [apiKey]);

  return isLoaded;
};

const MapSection = ({ latitude, longitude, storeName, imageBaseUrl }) => {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!latitude || !longitude || !window.google) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: Number(latitude), lng: Number(longitude) },
      zoom: 15,
      disableDefaultUI: true,
      mapTypeId: "roadmap",
    });
    new window.google.maps.Marker({
      position: { lat: Number(latitude), lng: Number(longitude) },
      map,
      title: storeName,
      icon: {
        url: `${imageBaseUrl}mPin.png`,
        scaledSize: new window.google.maps.Size(64, 64),
      },
    });
  }, [latitude, longitude, storeName, imageBaseUrl]);

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "460px" },
        height: { xs: 300, md: 522 }, // Fixed height to match UI
        borderRadius: "12px", // Reduced border radius to match UI
        overflow: "hidden",
        background: "#f2f3f0",
        boxShadow: "0 4px 20px 0 rgba(52, 80, 68, 0.1)", // Lighter shadow
        position: "relative",
      }}
    >
      <Box
        ref={mapRef}
        sx={{
          width: "100%",
          height: "100%", // Use full container height
        }}
      />
    </Box>
  );
};

const GlobalViewMorePage = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;
  const [storeDetails, setStoreDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isGoogleMapsLoaded = useGoogleMaps(GOOGLE_MAPS_API_KEY);

  const queryParams = new URLSearchParams(location.search);
  const storecode = queryParams.get("storecode");

  useEffect(() => {
    const fetchStoreDetails = async () => {
      try {
        const response = await storesAPI.getStoreDetails(storecode);
        if (response.success) {
          setStoreDetails(response.data);
        } else {
          setError("Failed to fetch store details");
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch store details");
        setLoading(false);
      }
    };
    if (storecode) fetchStoreDetails();
  }, [storecode]);

  const styles = {
    root: {
      display: "flex",
      flexDirection: "column",
    },
    header: {
      position: "relative",
      width: "100%",
      height: { xs: 280, md: 400 }, // Fixed height instead of minHeight
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    },
    headerBgImage: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%", // Use full container height
      objectFit: "cover",
      zIndex: 1,
    },
    headerOverlay: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0, 0, 0, 0.4)", // Added overlay for better text readability
      zIndex: 2,
    },
    headerContent: {
      position: "relative",
      zIndex: 3,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      py: 3, // Reduced padding
      gap: 2, // Reduced gap
    },
    storeTitle: {
      fontSize: { xs: 24, md: 40 }, // Slightly smaller to match UI
      fontWeight: 700,
      mb: 1.5, // Reduced margin
      color: "white",
      textAlign: "center",
      letterSpacing: 1,
      textTransform: "uppercase",
    },
    headerButtonsRow: {
      display: "flex",
      gap: 2,
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      mt: 1,
    },
    scheduleButton: {
      fontFamily: globalStyles.fonts.body1,
      display: "flex",
      alignItems: "center",
      gap: 1,
      bgcolor: "#fff",
      color: globalStyles.colors.army_green,
      borderRadius: "8px", // Slightly more rounded
      fontWeight: 600,
      fontSize: "0.95rem", // Slightly smaller
      minWidth: 160, // Reduced width
      px: 2,
      py: 1,
      textTransform: "none",
      '&:hover': {
        bgcolor: "#F3FBF7",
      },
    },
    phoneButton: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      border: "2px solid #fff",
      color: "#fff",
      borderRadius: "8px", // Match schedule button
      fontWeight: 700,
      fontSize: "0.95rem", // Slightly smaller
      minWidth: 160, // Reduced width
      px: 2,
      py: 1,
      textTransform: "none",
      '&:hover': {
        bgcolor: "#4CAF50",
        color: "#fff",
      },
    },
  };

  if (loading) {
    return (
      <Typography sx={{ textAlign: "center", py: 10 }}>
        {t("common.loading")}
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography sx={{ textAlign: "center", color: "error.main", py: 10 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box sx={styles.root}>
      {/* HEADER */}
      <Box sx={styles.header}>
        <Box
          component="img"
          src={`${imageBaseUrl}viewmorebanner.png`}
          alt={t("viewMore.alt.storefront")}
          sx={styles.headerBgImage}
        />
        <Box sx={styles.headerOverlay} />
        <Box sx={styles.headerContent}>
          <Typography variant="h1_gothic" sx={styles.storeTitle}>
            {storeDetails?.name}
          </Typography>
          <Box sx={styles.headerButtonsRow}>
            <Button sx={styles.scheduleButton}>
              💚 {t("Schedule Free Pickup") || "Schedule Free Pickup"}
            </Button>
            <Button
              component="a"
              href={storeDetails?.mobile ? `tel:${storeDetails.mobile}` : undefined}
              startIcon={<CallIcon />}
              sx={styles.phoneButton}
            >
              {storeDetails?.mobile}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* MAIN CONTENT SECTION */}
      <Box
        sx={{
          width: "100%",
          bgcolor: "#fff",
          py: { xs: 4, md: 6 }, // Reduced padding to match UI spacing
          position: "relative",
        }}
      >
        <Box
          sx={{
            mx: "auto",
            maxWidth: "1200px", // Added max width constraint
            width: { xs: '95%', sm: '90%', md: '85%' },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 6 }, // Adjusted gap
            px: { xs: 2, md: 3 },
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: "center",
          }}
        >
          {/* MAP SECTION */}
          <Box
            sx={{
              flex: { xs: "none", md: 1 },
              width: { xs: "100%", md: "auto" },
              maxWidth: { xs: "460px", md: "none" },
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              mb: { xs: 2, md: 0 },
            }}
          >
            {isGoogleMapsLoaded &&
            storeDetails?.latitude &&
            storeDetails?.longitude ? (
              <MapSection
                latitude={storeDetails.latitude}
                longitude={storeDetails.longitude}
                storeName={storeDetails.name}
                imageBaseUrl={imageBaseUrl}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: "100%", md: "460px" },
                  height: { xs: 300, md: 400 }, // Match MapSection height
                  borderRadius: "12px",
                  background: "#f2f3f0",
                  color: "#888",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  fontFamily: globalStyles.fonts.primary,
                }}
              >
                Loading Map...
              </Box>
            )}
          </Box>

          {/* INFO SECTION */}
          <Box
            sx={{
              flex: { xs: "none", md: 1 },
              width: { xs: "100%", md: "auto" },
              maxWidth: { xs: "460px", md: "none" },
              p: { xs: 2, md: 3 }, // Reduced padding
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2, // Added consistent gap
              xs: "24px", sm: "38px", md: "40px"
            }}
          >
            {/* Store Title */}
            <Typography
              variant="h1_gothic" 
            >
              {storeDetails?.name}
            </Typography>

         

            {/* Divider */}
            <Box
              sx={{
                width: "100%",
                borderTop: "1px solid #E2E7DE", // Thinner border
                mb: 2,
              }}
            />

            {/* Contact Information */}
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Location */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <Box 
                  component="img" 
                  src={`${imageBaseUrl}vmlocation.png`} 
                  alt="Location Icon" 
                  sx={{width:getResponsiveSize(50), height:getResponsiveHeight(50)}}
                />
                <Typography  variant="body2" sx={ {fontSize: { xs: "12px", sm: "18px", md: "18px" }}}>
                  {storeDetails?.address}
                </Typography>
              </Box>

              {/* Phone */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <Box 
                  component="img" 
                  src={`${imageBaseUrl}vmphone.png`} 
                  alt="Phone Icon" 
                     sx={{width:getResponsiveSize(50), height:getResponsiveHeight(50)}}
                />
                <Typography
                  component="a"
                  href={`tel:${storeDetails?.mobile}`}
                 variant="body2" sx={ {fontSize: { xs: "12px", sm: "18px", md: "18px" }}}
                >
                 
                  {storeDetails?.mobile}
                </Typography>
              </Box>

              {/* Email */}
             
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                  }}
                >
                  <Box 
                    component="img" 
                    src={`${imageBaseUrl}vmmail.png`} 
                    alt="Email Icon" 
                       sx={{width:getResponsiveSize(50), height:getResponsiveHeight(50)}}
                   
                  />
                  <Typography
                    component="a"
                    href={`mailto:${storeDetails?.email}`}
                    variant="body1"
                  >
                   support@uclean.in
                  </Typography>
                </Box>
             
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 3,
                width: "100%",
                flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
                alignItems: "stretch", // Full width on mobile
              }}
            >
              <Button
                variant="contained"
                sx={{
                  background: globalStyles.colors.army_green,
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.9rem", // Slightly smaller
                  borderRadius: "8px",
                  px: 2,
                  py: 1.2,
                  textTransform: "none",
                  flex: { xs: "none", sm: 1 }, // Equal width on desktop
                  "&:hover": { background: "#4CAF50" },
                }}
              >
                  💚 Schedule Free Pickup
              </Button>
              <Button
                variant="outlined"
                sx={{
                  color: globalStyles.colors.army_green,
                  border: `1.5px solid ${globalStyles.colors.army_green}`,
                  fontWeight: 600,
                  fontSize: "0.9rem", // Slightly smaller
                  borderRadius: "8px",
                  px: 2,
                  py: 1.2,
                  textTransform: "none",
                  background: "#fff",
                  flex: { xs: "none", sm: 1 }, // Equal width on desktop
                  "&:hover": {
                    borderColor: "#4CAF50",
                    background: "#F3FBF7",
                  },
                }}
              >
                View Store Pricelist
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer Sections */}
      <ServicesSection countryCode="uae" />
      <PromoSection countryCode="uae" />
      <FreeDeliveryBanner />
      <News />
      <Footer />
    </Box>
  );
};

export default GlobalViewMorePage;